/**
 * PATH: apps/quote/src/lib/api/quote-import-api.ts
 * PURPOSE: Client for the AI external-quote import endpoints.
 * WHY: Operators paste/upload an external quote; AI maps it into our format
 *      and (optionally) feeds the job's cost-centre BI.
 * FLOW: parse(text|file) -> preview -> (operator edits) -> commit -> quote id
 */
import { getApiClient, withOrgConfig } from '../quote-api-helpers'

export interface ImportLineSplit {
  labour_cost: string
  material_cost: string
  plant_cost: string
  subcon_cost: string
  labour_chargeout: string
  material_chargeout: string
  plant_chargeout: string
  subcon_chargeout: string
  labour_hours: string
  labour_days: string
  chargeout_total: string
  cost_total: string
  basis: string
  confidence: number
}

export interface ImportLineItem {
  location?: string
  description: string
  quantity?: number
  unit?: string
  unit_price?: number
  line_total?: number
  category_hint?: string
  confidence?: number
  _split: ImportLineSplit
}

export interface ImportSection {
  title: string
  items: ImportLineItem[]
}

export interface ImportPreview {
  quote_meta: {
    client_name?: string
    project_name?: string
    project_address?: string
    quote_reference?: string
    currency?: string
    notes?: string
  }
  sections: ImportSection[]
  totals: Record<string, string>
  warnings: string[]
  ai_model: string
}

export interface CommitResponse {
  quote_id: string
  quote_number: string
  status: string
}

export const quoteImportApi = {
  /** Parse pasted text into an editable preview (no DB writes). */
  async parseText(text: string): Promise<ImportPreview> {
    const client = getApiClient()
    const res = await client.post<ImportPreview>(
      '/quotebuilder/quote-import/parse/', { text }, withOrgConfig(),
    )
    return res.data
  },

  /** Parse an uploaded document (PDF/Excel/CSV/...) into an editable preview. */
  async parseFile(file: File): Promise<ImportPreview> {
    const client = getApiClient()
    const form = new FormData()
    form.append('file', file)
    const res = await client.post<ImportPreview>(
      '/quotebuilder/quote-import/parse/', form,
      withOrgConfig({ headers: { 'Content-Type': 'multipart/form-data' } }),
    )
    return res.data
  },

  /** Attach an Excel quote to a job using the quote-builder extractor. */
  async uploadQuoteSheetToJob(jobId: string | number, file: File): Promise<{ id?: number }> {
    const client = getApiClient()
    const form = new FormData()
    form.append('file', file)
    form.append('main_quote', 'true')
    form.append('allow_ai', 'false')
    const res = await client.post<{ id?: number }>(
      `/v1/jobs/${jobId}/quote-sheets/upload/`,
      form,
      withOrgConfig({
        timeout: 120_000,
        // Default client stamps application/json. Leave Content-Type unset
        // so the browser sets the multipart boundary.
        headers: { 'Content-Type': undefined },
      }),
    )
    return res.data
  },

  /** Persist an operator-reviewed preview as a quotebuilder Quote. */
  async commit(args: {
    preview: ImportPreview
    jobId?: string | number | null
    useAsJobBudget?: boolean
  }): Promise<CommitResponse> {
    const client = getApiClient()
    const res = await client.post<CommitResponse>(
      '/quotebuilder/quote-import/commit/',
      {
        preview: args.preview,
        job_id: args.jobId || null,
        use_as_job_budget: args.useAsJobBudget !== false,
      },
      withOrgConfig(),
    )
    return res.data
  },
}
